import type { TimelineScale } from './useTimelineScale';

interface TimeRulerProps {
  scale: TimelineScale;
  playheadTime: number;
  onSeek: (time: number) => void;
}

export function TimeRuler({ scale, playheadTime, onSeek }: TimeRulerProps) {
  const { duration, pixelsPerSecond, totalWidth, timeToX } = scale;

  // Determine tick interval based on zoom level
  const tickInterval = getTickInterval(pixelsPerSecond);
  const subTickCount = getSubTickCount(tickInterval);

  const ticks: { time: number; major: boolean }[] = [];
  for (let t = 0; t <= duration; t += tickInterval / subTickCount) {
    const rounded = Math.round(t * 1000) / 1000; // avoid float drift
    ticks.push({
      time: rounded,
      major: Math.abs(rounded % tickInterval) < 0.001,
    });
  }

  const seekFromEvent = (e: MouseEvent | React.MouseEvent, el: HTMLDivElement) => {
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left + el.scrollLeft;
    onSeek(Math.max(0, Math.min(scale.xToTime(x), duration)));
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    seekFromEvent(e, el);

    const handleMove = (moveEvent: MouseEvent) => seekFromEvent(moveEvent, el);
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: 'relative',
        height: 28,
        minWidth: totalWidth,
        background: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      {ticks.map((tick) => (
        <div
          key={tick.time}
          style={{
            position: 'absolute',
            left: timeToX(tick.time),
            top: tick.major ? 0 : 16,
            width: 1,
            height: tick.major ? 28 : 12,
            background: tick.major ? '#94a3b8' : '#cbd5e1',
          }}
        >
          {tick.major && (
            <span
              style={{
                position: 'absolute',
                left: 4,
                top: 2,
                fontSize: 10,
                color: '#64748b',
                whiteSpace: 'nowrap',
                fontFamily: 'system-ui, sans-serif',
                fontWeight: 500,
              }}
            >
              {formatTime(tick.time)}
            </span>
          )}
        </div>
      ))}

      {/* Playhead marker on ruler */}
      <div
        style={{
          position: 'absolute',
          left: timeToX(playheadTime) - 5,
          top: 0,
          width: 0,
          height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: '8px solid #2563eb',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

function getTickInterval(pps: number): number {
  if (pps >= 200) return 0.5;
  if (pps >= 80) return 1;
  if (pps >= 30) return 2;
  if (pps >= 15) return 5;
  return 10;
}

function getSubTickCount(interval: number): number {
  if (interval <= 1) return 4;
  if (interval <= 2) return 4;
  if (interval <= 5) return 5;
  return 5;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins > 0) {
    return `${mins}:${secs.toFixed(0).padStart(2, '0')}`;
  }
  return `${secs.toFixed(secs % 1 === 0 ? 0 : 1)}s`;
}
